import { useReduxState } from 'hooks';
import { ApiDownloadButton, MoreOptions } from 'components/common';
import { useBoxContext } from 'components/common/box';
import { UsersSendMessage } from 'components/users';
import { CalendarEventParticipantsSendFeedbackEmail } from 'components/calendar/feedback';
import {
  CalendarEventParticipantsAdd,
  CalendarEventParticipantsEditCustomAttributes,
  CalendarEventParticipantsRemove,
  CalendarEventParticipantsToggleParticipation
} from '.';

import type { GetEmailTemplatesByTypeRes } from 'shared-types/responses';

interface Props {
  existingUserIds: number[];
  feedbackEmailTemplates: GetEmailTemplatesByTypeRes['emailTemplates'];
  matchingUserIds: number[];
  updateUsers: () => Promise<void>;
}

const CalendarEventParticipantsAddedHeader: React.FunctionComponent<Props> = ({
  existingUserIds,
  feedbackEmailTemplates,
  matchingUserIds,
  updateUsers
}) => {
  const {
    messages,
    pageState: { calendarEvent },
    serverConfig: { adminUrl }
  } = useReduxState();

  const { selectedItems } = useBoxContext();

  const selectedUserIds = Object.keys(selectedItems).map((key) => Number(key));

  const selectedOrMatchingUserIds = selectedUserIds?.length
    ? selectedUserIds
    : matchingUserIds;

  return (
    <div style={{ alignItems: 'center', display: 'flex', gap: 10 }}>
      <UsersSendMessage
        buttonSize="header"
        emailButtonTarget={`${adminUrl}/calendar/${calendarEvent.id}`}
        id="calendar-event-participants-added-header-send-message"
        includeGuestUsersWithEmail={true}
        userSelectionParams={{
          excludeUsersWithoutEmail: true,
          userSelector: 'emails',
          ...(selectedUserIds.length
            ? { selectedUserIds }
            : { selectedUserIds: matchingUserIds })
        }}
      />

      <CalendarEventParticipantsAdd
        allowGroupSnapshots={true}
        existingUserIds={existingUserIds}
        updateUsers={updateUsers}
      />

      <MoreOptions category="lighter" styleContentButtons={false}>
        <ApiDownloadButton
          disabled={!matchingUserIds?.length}
          filename={`${calendarEvent.title}.xlsx`}
          icon="file-excel"
          isDropdown={true}
          packet={{ userIds: selectedOrMatchingUserIds }}
          text={messages.commonExportToExcel}
          url={`/admin/calendar-events/${calendarEvent.id}/excel-export`}
        />

        <CalendarEventParticipantsSendFeedbackEmail
          emailTemplates={feedbackEmailTemplates}
          selectedUserIds={selectedUserIds}
          updateUsers={updateUsers}
        />

        <CalendarEventParticipantsEditCustomAttributes
          selectedUserIds={selectedOrMatchingUserIds}
          updateUsers={updateUsers}
        />

        <CalendarEventParticipantsToggleParticipation
          action="grant"
          selectedUserIds={selectedOrMatchingUserIds}
          updateUsers={updateUsers}
        />

        <CalendarEventParticipantsToggleParticipation
          action="remove"
          selectedUserIds={selectedOrMatchingUserIds}
          updateUsers={updateUsers}
        />

        <CalendarEventParticipantsRemove
          selectedUserIds={selectedOrMatchingUserIds}
          status="added"
          updateUsers={updateUsers}
        />
      </MoreOptions>
    </div>
  );
};

export default CalendarEventParticipantsAddedHeader;
